import LoadingBar from '@amzn/awsui-chat-components-core/loading-bar';
import { Box, Table, TableProps, Tabs } from '@amzn/awsui-components-core';
import { faCalendarDays } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo } from 'react';
import { ErrorContainer } from 'src/components/fintech-ops/FinTechOpsCommonElements';
import { StatusType } from 'src/components/fintech-ops/sas/SasInterfaces';
import { LoadingSpinner } from 'src/components/generic-components/LoadingSpinner';
import { getCurrentTime, getMomentTime } from 'src/utilities/DateTimeUtilities';
import { FinanceCalendarDatum, FinanceCalendarDataTypes, getScenarioWithNearestActivity } from './utils/financeCalendarTransformers';
import './finance-calendar.scss';

export interface FinanceCalendarContentProps {
  status: StatusType;
  data: FinanceCalendarDataTypes[];
  highlightEvent?: { scenario: string; event: string } | null;
}

const isToday = (date: Date): boolean => {
  return getMomentTime(date.toISOString()).isSame(getCurrentTime(), 'day');
};

const isTodayOrFuture = (date: Date): boolean => {
  return getMomentTime(date.toISOString()).isSameOrAfter(getCurrentTime(), 'day');
};

const formatDateDisplay = (date: Date): string => {
  if (isToday(date)) return 'Today';
  return getMomentTime(date.toISOString()).format('MMM D');
};

const formatTimeDisplay = (date: Date): string => {
  return getMomentTime(date.toISOString()).format('h:mm A');
};

const financeCalendarColumnDefs: TableProps.ColumnDefinition<FinanceCalendarDatum>[] = [
  {
    id: 'date',
    header: '',
    cell: (item: FinanceCalendarDatum) => (
      <Box fontWeight={isToday(item.date) ? 'bold' : 'normal'}>
        {isToday(item.date) && <FontAwesomeIcon icon={faCalendarDays} className="finance-calendar-today-icon" />}
        {formatDateDisplay(item.date)}
      </Box>
    ),
    width: 80
  },
  {
    id: 'time',
    header: '',
    cell: (item: FinanceCalendarDatum) => <Box>{formatTimeDisplay(item.date)}</Box>,
    width: 80
  },
  {
    id: 'event',
    header: '',
    cell: (item: FinanceCalendarDatum) => <Box>{item.event}</Box>
  }
];

const FinanceCalendarContent: React.FC<FinanceCalendarContentProps> = ({ status, data, highlightEvent }) => {
  const [activeTab, setActiveTab] = React.useState('');
  const [highlightedEvent, setHighlightedEvent] = React.useState<string | null>(null);
  const userSelectedRef = React.useRef(false);

  React.useEffect(() => {
    if (data.length === 0) return;
    // Auto-select best tab unless user has manually picked one
    if (!userSelectedRef.current) {
      setActiveTab(getScenarioWithNearestActivity(data));
    }
  }, [data]);

  React.useEffect(() => {
    if (!highlightEvent) return;
    setActiveTab(highlightEvent.scenario);
    setHighlightedEvent(highlightEvent.event);
    const timer = setTimeout(() => setHighlightedEvent(null), 2500);
    return () => clearTimeout(timer);
  }, [highlightEvent]);

  const highlightColumnDefs = useMemo(
    () =>
      financeCalendarColumnDefs.map((col) =>
        col.id === 'event'
          ? {
              ...col,
              cell: (item: FinanceCalendarDatum) => (
                <Box>
                  <span className={item.event === highlightedEvent ? 'finance-calendar-event--highlight' : ''}>{item.event}</span>
                </Box>
              )
            }
          : col
      ),
    [highlightedEvent]
  );

  const tabs = useMemo(
    () =>
      data.map((scenarioGroup) => {
        const upcomingItems = scenarioGroup.table_item.filter((item) => isTodayOrFuture(item.date));
        return {
          id: scenarioGroup.scenario,
          label: scenarioGroup.scenario,
          content: (
            <div className="finance-calendar-table">
              <Table
                variant="embedded"
                columnDefinitions={highlightColumnDefs}
                items={upcomingItems}
                empty={
                  <Box textAlign="center" color="inherit" padding="l">
                    <b>No upcoming events</b>
                    <Box variant="p" color="inherit">
                      There are no upcoming {scenarioGroup.scenario} events scheduled.
                    </Box>
                  </Box>
                }
              />
            </div>
          )
        };
      }),
    [data, highlightColumnDefs]
  );

  if (status === 'loading' && data.length < 1) {
    return <LoadingSpinner />;
  } else if (status === 'error') {
    return <ErrorContainer />;
  } else if (status === 'pending') {
    return <LoadingBar variant="gen-ai" />;
  }

  return (
    <Tabs
      tabs={tabs}
      activeTabId={activeTab}
      onChange={({ detail }) => {
        userSelectedRef.current = true;
        setActiveTab(detail.activeTabId);
      }}
    />
  );
};

export default FinanceCalendarContent;
